import { useCallback, useEffect, useRef, useState } from 'react'
import { computeCoverObjectPositionY } from '@/utils/challengeCover.utils'

// Keeps a cover image's chosen vertical focal point centered across containers
// of differing aspect ratios. Attach `containerRef` to the clipping box and
// `imgRef`/`onImgLoad` to the <img>; apply `objectPositionY` as the CSS
// object-position Y. Recomputes on load and on container resize, and falls back
// to the raw focal value until the real dimensions are known (e.g. in tests/SSR).
export const useCoverObjectPositionY = (focusY?: number) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [objectPositionY, setObjectPositionY] = useState(focusY ?? 50)

  const recompute = useCallback(() => {
    const el = containerRef.current
    const img = imgRef.current
    if (el === null || img === null) return
    const cw = el.clientWidth
    const ch = el.clientHeight
    if (cw === 0 || ch === 0 || img.naturalWidth === 0 || img.naturalHeight === 0) return
    setObjectPositionY(
      computeCoverObjectPositionY(focusY ?? 50, cw / ch, img.naturalWidth / img.naturalHeight),
    )
  }, [focusY])

  useEffect(() => { recompute() }, [recompute])

  useEffect(() => {
    const el = containerRef.current
    if (el === null || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => recompute())
    observer.observe(el)
    return () => observer.disconnect()
  }, [recompute])

  return { containerRef, imgRef, onImgLoad: recompute, objectPositionY }
}
